/**
 * Notes & Attachments helpers — mirrors iOS NotesAttachmentsCardView
 * freeform files on a hidden system note holder per day; user notes as type `note`.
 */

import type {
  ItineraryDay,
  ItineraryItem,
  ItineraryItemAttachment,
  Trip,
  TripAttachmentRef,
} from '../types/client';
import { formatYmd, ymdFromUnknown } from './wallClock';

export const SYSTEM_ATTACHMENTS_NOTE_TITLE = 'System created attachments note';

export type NotesAttachmentScope = 'today' | 'city' | 'trip' | 'allTrip' | 'tripWide';

export interface CollectedAttachment {
  attachment: ItineraryItemAttachment;
  itemId: string;
  /** Empty for trip-wide attachments. */
  dayDate: string;
  cityId?: string;
  tripWide?: boolean;
}

/** @deprecated Prefer CollectedNote from note items */
export interface DayNoteEntry {
  dayDate: string;
  cityId?: string;
  text: string;
}

export interface CollectedNote {
  id: string;
  title: string;
  body: string;
  /** Empty for trip-wide notes. */
  dayDate: string;
  cityId?: string;
  tripWide?: boolean;
  /** Synthetic row from legacy day.notes (no item id yet until migrate-on-write). */
  legacyDayNotes?: boolean;
}

function newId(prefix: string): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function isSystemAttachmentsNote(item: ItineraryItem): boolean {
  return (
    item.title === SYSTEM_ATTACHMENTS_NOTE_TITLE || item.title === 'Attachments'
  );
}

export function isUserNote(item: ItineraryItem): boolean {
  return item.type === 'note' && !isSystemAttachmentsNote(item);
}

export function noteBody(item: ItineraryItem): string {
  const fromNotes = item.notes != null ? String(item.notes).trim() : '';
  if (fromNotes) return fromNotes;
  const fromMeta = item.metadata?.notes != null ? String(item.metadata.notes).trim() : '';
  return fromMeta;
}

/** Hide system holder and note items from Day View activity lists. */
export function visibleDayItems(items: ItineraryItem[]): ItineraryItem[] {
  return items.filter((i) => !isSystemAttachmentsNote(i) && i.type !== 'note');
}

function cloneTripDays(trip: Trip): ItineraryDay[] {
  return (trip.itineraryDays ?? []).map((d) => ({
    ...d,
    items: d.items.map((item) => ({
      ...item,
      attachments: item.attachments ? [...item.attachments] : undefined,
      metadata: item.metadata ? { ...item.metadata } : undefined,
    })),
  }));
}

function ensureDay(
  days: ItineraryDay[],
  dayDate: string,
  cityId?: string
): ItineraryDay {
  // Prefer date + city match so multi-city same calendar day stays distinct.
  let idx = -1;
  if (cityId) {
    idx = days.findIndex((d) => d.date === dayDate && d.cityId === cityId);
  }
  if (idx < 0) {
    idx = days.findIndex(
      (d) => d.date === dayDate && (!cityId || !d.cityId || d.cityId === cityId)
    );
  }
  if (idx >= 0) {
    const day = days[idx];
    if (cityId && !day.cityId) {
      days[idx] = { ...day, cityId };
      return days[idx];
    }
    return day;
  }
  const created: ItineraryDay = {
    date: dayDate,
    items: [],
    cityId,
  };
  days.push(created);
  return created;
}

function cloneTripItems(trip: Trip): ItineraryItem[] {
  return (trip.tripItems ?? []).map((item) => ({
    ...item,
    attachments: item.attachments ? [...item.attachments] : undefined,
    metadata: item.metadata ? { ...item.metadata } : undefined,
  }));
}

/**
 * Find or create the hidden system note that holds freeform day attachments.
 * Returns updated trip + holder item id.
 */
export function ensureAttachmentHolder(
  trip: Trip,
  dayDate: string,
  cityId?: string
): { trip: Trip; holderId: string } {
  const days = cloneTripDays(trip);
  const day = ensureDay(days, dayDate, cityId);
  const existing = day.items.find(isSystemAttachmentsNote);
  if (existing) {
    return {
      trip: { ...trip, itineraryDays: days },
      holderId: existing.id,
    };
  }
  const holderId = newId(`holder-${dayDate}`);
  const holder: ItineraryItem = {
    id: holderId,
    title: SYSTEM_ATTACHMENTS_NOTE_TITLE,
    type: 'note',
    attachments: [],
  };
  day.items = [...day.items, holder];
  return {
    trip: { ...trip, itineraryDays: days },
    holderId,
  };
}

/** Find or create the trip-wide system attachment holder (schema 1.4 tripItems). */
export function ensureTripAttachmentHolder(trip: Trip): {
  trip: Trip;
  holderId: string;
} {
  const items = cloneTripItems(trip);
  const existing = items.find(isSystemAttachmentsNote);
  if (existing) {
    return {
      trip: { ...trip, tripItems: items },
      holderId: existing.id,
    };
  }
  const holderId = newId('holder-trip');
  const holder: ItineraryItem = {
    id: holderId,
    title: SYSTEM_ATTACHMENTS_NOTE_TITLE,
    type: 'note',
    attachments: [],
  };
  return {
    trip: { ...trip, tripItems: [...items, holder] },
    holderId,
  };
}

function daysMatchingScope(
  trip: Trip,
  scope: NotesAttachmentScope,
  ctx: { dayDate?: string; cityVisitId?: string }
): ItineraryDay[] {
  const days = trip.itineraryDays ?? [];
  switch (scope) {
    case 'today': {
      if (!ctx.dayDate) return [];
      return days.filter((d) => d.date === ctx.dayDate);
    }
    case 'city': {
      const cityId = ctx.cityVisitId;
      if (!cityId) {
        if (!ctx.dayDate) return [];
        const today = days.find((d) => d.date === ctx.dayDate);
        if (!today?.cityId) return today ? [today] : [];
        return days.filter((d) => d.cityId === today.cityId);
      }
      return days.filter((d) => d.cityId === cityId);
    }
    case 'trip':
    case 'allTrip':
      return days;
    case 'tripWide':
      return [];
    default:
      return days;
  }
}

function includeTripWide(scope: NotesAttachmentScope): boolean {
  return scope === 'tripWide' || scope === 'allTrip';
}

function includeDayScoped(scope: NotesAttachmentScope): boolean {
  return scope !== 'tripWide';
}

/**
 * Collect user note items (+ legacy day.notes bridge) for the scope.
 */
export function collectUserNotes(
  trip: Trip,
  scope: NotesAttachmentScope,
  ctx: { dayDate?: string; cityVisitId?: string }
): CollectedNote[] {
  const out: CollectedNote[] = [];
  if (includeDayScoped(scope)) {
    for (const d of daysMatchingScope(trip, scope, ctx)) {
      const userNotes = d.items.filter(isUserNote);
      for (const item of userNotes) {
        out.push({
          id: item.id,
          title: item.title,
          body: noteBody(item),
          dayDate: d.date,
          cityId: d.cityId,
        });
      }
      const legacy = d.notes != null ? String(d.notes).trim() : '';
      if (legacy && userNotes.length === 0) {
        out.push({
          id: `legacy:${d.date}:${d.cityId ?? ''}`,
          title: '',
          body: legacy,
          dayDate: d.date,
          cityId: d.cityId,
          legacyDayNotes: true,
        });
      }
    }
  }
  if (includeTripWide(scope)) {
    for (const item of trip.tripItems ?? []) {
      if (!isUserNote(item)) continue;
      out.push({
        id: item.id,
        title: item.title,
        body: noteBody(item),
        dayDate: '',
        tripWide: true,
      });
    }
  }
  return out;
}

/** @deprecated Use collectUserNotes — kept for transitional call sites/tests. */
export function collectDayNotes(
  trip: Trip,
  scope: NotesAttachmentScope,
  ctx: { dayDate?: string; cityVisitId?: string }
): DayNoteEntry[] {
  return collectUserNotes(trip, scope, ctx).map((n) => ({
    dayDate: n.dayDate,
    cityId: n.cityId,
    text: n.title ? (n.body ? `${n.title} – ${n.body}` : n.title) : n.body,
  }));
}

export function collectAttachments(
  trip: Trip,
  scope: NotesAttachmentScope,
  ctx: { dayDate?: string; cityVisitId?: string }
): CollectedAttachment[] {
  const out: CollectedAttachment[] = [];
  const seen = new Set<string>();
  if (includeDayScoped(scope)) {
    for (const day of daysMatchingScope(trip, scope, ctx)) {
      for (const item of day.items) {
        for (const attachment of item.attachments ?? []) {
          if (seen.has(attachment.id)) continue;
          seen.add(attachment.id);
          out.push({
            attachment,
            itemId: item.id,
            dayDate: day.date,
            cityId: day.cityId,
          });
        }
      }
    }
  }
  if (includeTripWide(scope)) {
    for (const item of trip.tripItems ?? []) {
      for (const attachment of item.attachments ?? []) {
        if (seen.has(attachment.id)) continue;
        seen.add(attachment.id);
        out.push({
          attachment,
          itemId: item.id,
          dayDate: '',
          tripWide: true,
        });
      }
    }
  }
  return out;
}

export function fileIdForAttachment(
  trip: Trip,
  attachmentId: string
): string | undefined {
  return trip.attachmentRefs?.find((r) => r.attachmentId === attachmentId)?.fileId;
}

/**
 * Attach a file to a specific itinerary item (activity / meal / transport / etc.).
 * Updates item.attachments and trip attachmentRefs / attachmentFileIds.
 */
export function attachFileToItem(
  trip: Trip,
  args: {
    itemId: string;
    fileId: string;
    fileName: string;
    fileSize: number;
    mimeType: string;
    attachmentId?: string;
    createdAt?: string;
  }
): Trip {
  const attachmentId = args.attachmentId ?? newId('att');
  const meta: ItineraryItemAttachment = {
    id: attachmentId,
    fileName: args.fileName,
    fileSize: args.fileSize,
    mimeType: args.mimeType,
    createdAt: args.createdAt ?? new Date().toISOString(),
  };

  let found = false;
  const days = cloneTripDays(trip).map((day) => ({
    ...day,
    items: day.items.map((item) => {
      if (item.id !== args.itemId) return item;
      found = true;
      const existingAtts = item.attachments ?? [];
      if (existingAtts.some((a) => a.id === attachmentId)) return item;
      return { ...item, attachments: [...existingAtts, meta] };
    }),
  }));

  let tripItems = trip.tripItems;
  if (!found && tripItems) {
    tripItems = cloneTripItems(trip).map((item) => {
      if (item.id !== args.itemId) return item;
      found = true;
      const existingAtts = item.attachments ?? [];
      if (existingAtts.some((a) => a.id === attachmentId)) return item;
      return { ...item, attachments: [...existingAtts, meta] };
    });
  }

  if (!found) return trip;

  return mergeAttachmentRefs(
    {
      ...trip,
      itineraryDays: days,
      ...(tripItems !== undefined ? { tripItems } : {}),
    },
    [{ attachmentId, fileId: args.fileId }]
  );
}

/** Merge attachment id → file id refs onto a trip without dropping existing refs. */
export function mergeAttachmentRefs(
  trip: Trip,
  refsToAdd: TripAttachmentRef[]
): Trip {
  if (!refsToAdd.length) return trip;
  const refs: TripAttachmentRef[] = [...(trip.attachmentRefs ?? [])];
  const fileIds = [...(trip.attachmentFileIds ?? [])];
  for (const r of refsToAdd) {
    if (!refs.some((x) => x.attachmentId === r.attachmentId)) {
      refs.push({ attachmentId: r.attachmentId, fileId: r.fileId });
    }
    if (!fileIds.includes(r.fileId)) {
      fileIds.push(r.fileId);
    }
  }
  return {
    ...trip,
    attachmentRefs: refs,
    attachmentFileIds: fileIds,
  };
}

/**
 * Apply a saved item's attachment list and drop orphaned refs for removed attachments
 * that previously belonged only to this item. New refs are merged in.
 */
export function applyItemAttachmentsOnSave(
  trip: Trip,
  args: {
    itemId: string;
    attachments: ItineraryItemAttachment[];
    /** New or retained attachmentId → fileId pairs for attachments on this item. */
    refs: TripAttachmentRef[];
  }
): Trip {
  let foundInDays = false;
  const days = cloneTripDays(trip).map((day) => ({
    ...day,
    items: day.items.map((item) => {
      if (item.id !== args.itemId) return item;
      foundInDays = true;
      return { ...item, attachments: [...args.attachments] };
    }),
  }));

  let tripItems = trip.tripItems;
  if (!foundInDays && tripItems) {
    tripItems = cloneTripItems(trip).map((item) =>
      item.id === args.itemId ? { ...item, attachments: [...args.attachments] } : item
    );
  }

  const previousIds = new Set<string>();
  for (const day of trip.itineraryDays ?? []) {
    const item = day.items.find((i) => i.id === args.itemId);
    for (const a of item?.attachments ?? []) previousIds.add(a.id);
  }
  for (const item of trip.tripItems ?? []) {
    if (item.id !== args.itemId) continue;
    for (const a of item.attachments ?? []) previousIds.add(a.id);
  }
  const nextIds = new Set(args.attachments.map((a) => a.id));
  const removedIds = Array.from(previousIds).filter((id) => !nextIds.has(id));

  let next: Trip = {
    ...trip,
    itineraryDays: days,
    ...(tripItems !== undefined ? { tripItems } : {}),
  };
  for (const id of removedIds) {
    next = removeAttachment(next, id);
  }
  return mergeAttachmentRefs(next, args.refs);
}

/**
 * Attach a file to the system holder for the given day (placement day),
 * or to the trip-wide holder when `tripWide` is true / `dayDate` is omitted.
 */
export function attachFileToHolder(
  trip: Trip,
  args: {
    dayDate?: string;
    cityId?: string;
    tripWide?: boolean;
    fileId: string;
    fileName: string;
    fileSize: number;
    mimeType: string;
    attachmentId?: string;
    createdAt?: string;
  }
): Trip {
  const useTripWide = Boolean(args.tripWide || !args.dayDate);
  if (useTripWide) {
    const { trip: withHolder, holderId } = ensureTripAttachmentHolder(trip);
    const items = cloneTripItems(withHolder);
    const holderIdx = items.findIndex((i) => i.id === holderId);
    if (holderIdx < 0) return withHolder;

    const attachmentId = args.attachmentId ?? newId('att');
    const meta: ItineraryItemAttachment = {
      id: attachmentId,
      fileName: args.fileName,
      fileSize: args.fileSize,
      mimeType: args.mimeType,
      createdAt: args.createdAt ?? new Date().toISOString(),
    };
    const holder = items[holderIdx];
    const existingAtts = holder.attachments ?? [];
    if (existingAtts.some((a) => a.id === attachmentId)) {
      return withHolder;
    }
    items[holderIdx] = {
      ...holder,
      type: 'note',
      attachments: [...existingAtts, meta],
    };
    return mergeAttachmentRefs(
      { ...withHolder, tripItems: items },
      [{ attachmentId, fileId: args.fileId }]
    );
  }

  const dayDate = args.dayDate!;
  const { trip: withHolder, holderId } = ensureAttachmentHolder(
    trip,
    dayDate,
    args.cityId
  );
  const days = cloneTripDays(withHolder);
  const day = ensureDay(days, dayDate, args.cityId);
  const holderIdx = day.items.findIndex((i) => i.id === holderId);
  if (holderIdx < 0) return withHolder;

  const attachmentId = args.attachmentId ?? newId('att');
  const meta: ItineraryItemAttachment = {
    id: attachmentId,
    fileName: args.fileName,
    fileSize: args.fileSize,
    mimeType: args.mimeType,
    createdAt: args.createdAt ?? new Date().toISOString(),
  };

  const holder = day.items[holderIdx];
  const existingAtts = holder.attachments ?? [];
  if (existingAtts.some((a) => a.id === attachmentId)) {
    return withHolder;
  }
  day.items[holderIdx] = {
    ...holder,
    type: 'note',
    attachments: [...existingAtts, meta],
  };

  return mergeAttachmentRefs(
    { ...withHolder, itineraryDays: days },
    [{ attachmentId, fileId: args.fileId }]
  );
}

export function removeAttachment(trip: Trip, attachmentId: string): Trip {
  const days = cloneTripDays(trip).map((day) => ({
    ...day,
    items: day.items.map((item) => {
      const atts = item.attachments;
      if (!atts?.length) return item;
      const next = atts.filter((a) => a.id !== attachmentId);
      if (next.length === atts.length) return item;
      return { ...item, attachments: next };
    }),
  }));

  let tripItems = trip.tripItems;
  if (tripItems) {
    tripItems = cloneTripItems(trip).map((item) => {
      const atts = item.attachments;
      if (!atts?.length) return item;
      const next = atts.filter((a) => a.id !== attachmentId);
      if (next.length === atts.length) return item;
      return { ...item, attachments: next };
    });
  }

  const removedRef = trip.attachmentRefs?.find((r) => r.attachmentId === attachmentId);
  const refs = (trip.attachmentRefs ?? []).filter((r) => r.attachmentId !== attachmentId);
  let fileIds = [...(trip.attachmentFileIds ?? [])];
  if (removedRef) {
    const stillUsed = refs.some((r) => r.fileId === removedRef.fileId);
    if (!stillUsed) {
      fileIds = fileIds.filter((id) => id !== removedRef.fileId);
    }
  }

  return {
    ...trip,
    itineraryDays: days,
    ...(tripItems !== undefined ? { tripItems } : {}),
    attachmentRefs: refs,
    attachmentFileIds: fileIds,
  };
}

/** Update day.notes for a placement day (legacy). Prefer addNoteItem / updateNoteItem. */
export function setDayNotes(trip: Trip, dayDate: string, notes: string, cityId?: string): Trip {
  const days = cloneTripDays(trip);
  const day = ensureDay(days, dayDate, cityId);
  day.notes = notes.trim() ? notes : null;
  return { ...trip, itineraryDays: days };
}

export function addNoteItem(
  trip: Trip,
  args: {
    dayDate?: string;
    cityId?: string;
    tripWide?: boolean;
    title: string;
    body?: string;
    id?: string;
  }
): Trip {
  const title = args.title.trim();
  if (!title) return trip;
  const body = (args.body ?? '').trim();
  const id = args.id ?? newId('note');
  const item: ItineraryItem = {
    id,
    title,
    type: 'note',
    notes: body || undefined,
    metadata: body ? { notes: body } : {},
  };

  if (args.tripWide || !args.dayDate) {
    const items = cloneTripItems(trip);
    return { ...trip, tripItems: [...items, item] };
  }

  const days = cloneTripDays(trip);
  const day = ensureDay(days, args.dayDate, args.cityId);
  day.items = [...day.items, item];
  return { ...trip, itineraryDays: days };
}

export function updateNoteItem(
  trip: Trip,
  args: {
    noteId: string;
    title: string;
    body?: string;
    /** When editing a legacy day.notes row, pass dayDate (+ cityId) to migrate. */
    legacyDayDate?: string;
    legacyCityId?: string;
  }
): Trip {
  const title = args.title.trim();
  if (!title) return trip;
  const body = (args.body ?? '').trim();

  if (args.legacyDayDate) {
    let next = addNoteItem(trip, {
      dayDate: args.legacyDayDate,
      cityId: args.legacyCityId,
      title,
      body,
      id: args.noteId.startsWith('legacy:') ? undefined : args.noteId,
    });
    next = setDayNotes(next, args.legacyDayDate, '', args.legacyCityId);
    return next;
  }

  const patchItem = (item: ItineraryItem): ItineraryItem => {
    if (item.id !== args.noteId) return item;
    const metadata = { ...(item.metadata ?? {}) };
    if (body) metadata.notes = body;
    else delete metadata.notes;
    return {
      ...item,
      title,
      type: 'note' as const,
      notes: body || undefined,
      metadata,
    };
  };

  const days = cloneTripDays(trip).map((day) => ({
    ...day,
    items: day.items.map(patchItem),
  }));

  let tripItems = trip.tripItems;
  if (tripItems?.some((i) => i.id === args.noteId)) {
    tripItems = cloneTripItems(trip).map(patchItem);
  }

  return {
    ...trip,
    itineraryDays: days,
    ...(tripItems !== undefined ? { tripItems } : {}),
  };
}

export function deleteNoteItem(trip: Trip, noteId: string): Trip {
  if (noteId.startsWith('legacy:')) {
    const rest = noteId.slice('legacy:'.length);
    const sep = rest.indexOf(':');
    const dayDate = sep >= 0 ? rest.slice(0, sep) : rest;
    const cityId = sep >= 0 ? rest.slice(sep + 1) : '';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dayDate)) return trip;
    return setDayNotes(trip, dayDate, '', cityId || undefined);
  }

  const days = cloneTripDays(trip).map((day) => ({
    ...day,
    items: day.items.filter((item) => item.id !== noteId),
  }));

  let tripItems = trip.tripItems;
  if (tripItems) {
    tripItems = tripItems.filter((item) => item.id !== noteId);
  }

  return {
    ...trip,
    itineraryDays: days,
    ...(tripItems !== undefined ? { tripItems } : {}),
  };
}

/**
 * File IDs for attachments present on a city's itinerary days
 * (for granular cityVisit-v1 attachmentFileIds).
 */
export function cityAttachmentFileIds(trip: Trip, cityVisitId: string): string[] {
  const collected = collectAttachments(trip, 'city', { cityVisitId });
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const c of collected) {
    const fileId = fileIdForAttachment(trip, c.attachment.id);
    if (fileId && !seen.has(fileId)) {
      seen.add(fileId);
      ids.push(fileId);
    }
  }
  return ids;
}

export function formatNoteDateLabel(dayDate: string): string {
  if (!dayDate) return 'Trip-wide';
  const ymd = ymdFromUnknown(dayDate);
  if (!ymd) return dayDate;
  return formatYmd(ymd, { month: 'short', day: 'numeric', year: 'numeric' });
}
